import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, Calendar, Mail, Globe, MapPin } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const serviceOptions = [
    'Studio Build',
    'Ongoing Broadcast Partner',
    'Live Production',
    'Video Production',
    'Virtual Events',
    'Post Production'
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Include service in message if selected
      const messageWithService = contactForm.service 
        ? `[Service Interest: ${contactForm.service}]\n\n${contactForm.message}`
        : contactForm.message;
      
      // Save to database - emails are sent automatically by the backend
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: messageWithService
      });
      
      toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
      setContactForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian-paper" data-testid="contact-section" ref={ref}>
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent" />
      
      {/* Glow orbs */}
      <div className="glow-orb glow-orb-blue w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 -top-24 sm:-top-36 md:-top-48 left-1/4 opacity-10" />
      <div className="glow-orb glow-orb-pink w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bottom-0 right-1/4 opacity-10" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            Get Started
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 sm:mb-6" data-testid="contact-heading">
            Ready to <span className="text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}>Elevate</span> Your Production?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white mb-6 sm:mb-8">
            Communicate your message with clarity, consistency, and long-term stability.
          </p>
          
          {/* Schedule Your Call Button */}
          <a
            href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-neon btn-neon-gold inline-flex items-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
            data-testid="contact-schedule-btn"
          >
            <Calendar size={18} />
            <span>Schedule Your Call</span>
          </a>
        </motion.div>

        {/* Ask a Question Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-6 sm:mb-8"
        >
          <p className="text-sm sm:text-base text-white">
            Not ready to schedule? Ask us a question below.
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-2xl mx-auto"
        >
          {/* Contact Form - Always visible, no tabs */}
          <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-white uppercase tracking-wider mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="input-neon w-full"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white uppercase tracking-wider mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="input-neon w-full"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-white uppercase tracking-wider mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  required
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="input-neon w-full"
                  placeholder="(555) 123-4567"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white uppercase tracking-wider mb-2">
                  Select Your Production Need
                  </label>
                  <select
                    value={contactForm.service}
                    onChange={(e) => setContactForm({ ...contactForm, service: e.target.value })}
                    className="input-neon w-full cursor-pointer"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white uppercase tracking-wider mb-2">
                  Share your vision, goals or event details (Optional)
                </label>
                <textarea
                  rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="input-neon w-full resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                />
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-neon btn-neon-gold w-full"
                  data-testid="contact-submit-btn"
                >
                  <span className="flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </span>
                </button>
                <p className="text-xs text-white/40 mt-3">
                  We'll respond within 24 hours.
                </p>
              </div>
            </form>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 sm:mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 text-center"
        >
          <div className="p-4 sm:p-5 md:p-6 border border-white/5 hover:border-neon-pink/30 transition-colors">
            <Mail className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-neon-pink mx-auto mb-3 sm:mb-4" />
            <h3 className="font-heading text-base sm:text-lg font-bold uppercase mb-1 sm:mb-2">Email</h3>
            <a href="mailto:info@kingdomvibestudios.com" className="text-xs sm:text-sm text-white/50 hover:text-neon-pink transition-colors break-all">
              info@kingdomvibestudios.com
            </a>
          </div>
          <div className="p-4 sm:p-5 md:p-6 border border-white/5 hover:border-neon-blue/30 transition-colors">
            <Globe className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-neon-blue mx-auto mb-3 sm:mb-4" />
            <h3 className="font-heading text-base sm:text-lg font-bold uppercase mb-1 sm:mb-2">Service Area</h3>
            <p className="text-sm sm:text-base text-white/50">
              Serving Clients Nationwide
            </p>
          </div>
          <div className="p-4 sm:p-5 md:p-6 border border-white/5 hover:border-amber-400/30 transition-colors">
            <MapPin className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-amber-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="font-heading text-base sm:text-lg font-bold uppercase mb-1 sm:mb-2">Location</h3>
            <p className="text-sm sm:text-base text-white/50">
              Summerville, SC
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
